import React, { useState } from 'react';

interface BetaSignupFormProps {
  isOpen: boolean;
  onClose: () => void;
  onFormSubmitted?: (firstName: string) => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  heardOn: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  heardOn?: string;
}

const BetaSignupForm: React.FC<BetaSignupFormProps> = ({ isOpen, onClose, onFormSubmitted }) => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    heardOn: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUSPhone = (phone: string): boolean => {
    if (!phone.trim()) return true; // Optional field
    const phoneRegex = /^(\+1)?[-.\s]?(\()?[0-9]{3}(\)?[-.\s]?)[0-9]{3}[-.\s]?[0-9]{4}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone.trim() && !validateUSPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid US phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setGeneralError(null);

    try {
      // Submit to backend API
      const response = await fetch('/api/beta-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          heardOn: formData.heardOn
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit signup');
      }

      console.log('✅ Beta signup successful:', data);

      // Show success toast
      setShowSuccess(true);

      // Trigger Ellie to thank the user
      if (onFormSubmitted) {
        onFormSubmitted(formData.firstName);
      }

      // Close the form quickly
      setTimeout(() => {
        handleClose();
      }, 500);

      // Hide success toast after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);

    } catch (error) {
      console.error('Beta signup error:', error);
      setGeneralError(error instanceof Error ? error.message : 'Failed to submit signup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Apply phone number formatting for phone field
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleClose = () => {
    setFormData({ firstName: '', lastName: '', email: '', phone: '', heardOn: '' });
    setErrors({});
    setGeneralError(null);
    setIsSubmitting(false);
    // Don't reset showSuccess here - let the timeout handle it
    onClose();
  };

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>

      {/* Success Toast - Top Right - Always rendered independently */}
      {showSuccess && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 20000,
          padding: '20px',
          backgroundColor: '#1a1a1a',
          border: '2px solid #4ecdc4',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          animation: 'slideInRight 0.3s ease-out',
          minWidth: '300px',
          maxWidth: '400px'
        }}>
          <span style={{ fontSize: '28px' }}>✅</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#4ecdc4', fontWeight: '600', fontSize: '16px' }}>
              Success!
            </div>
            <div style={{ color: '#ccc', fontSize: '13px', marginTop: '4px' }}>
              You've been added to the beta list. We'll be in touch soon!
            </div>
          </div>
        </div>
      )}

    {isOpen && (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        padding: '30px',
        borderRadius: '15px',
        maxWidth: '400px',
        width: '100%',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Close Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{
            color: 'white',
            margin: 0,
            fontSize: '20px',
            fontWeight: '600'
          }}>
            🚀 Join the Beta
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#ccc',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '5px'
            }}
          >
            ✕
          </button>
        </div>

        {(
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{
                display: 'block',
                color: '#ccc',
                fontSize: '12px',
                marginBottom: '5px',
                fontWeight: '500'
              }}>
                First Name *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: `1px solid ${errors.firstName ? '#ff6b6b' : 'rgba(255, 255, 255, 0.2)'}`,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter your first name"
              />
              {errors.firstName && (
                <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '3px' }}>
                  {errors.firstName}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{
                display: 'block',
                color: '#ccc',
                fontSize: '12px',
                marginBottom: '5px',
                fontWeight: '500'
              }}>
                Last Name *
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: `1px solid ${errors.lastName ? '#ff6b6b' : 'rgba(255, 255, 255, 0.2)'}`,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter your last name"
              />
              {errors.lastName && (
                <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '3px' }}>
                  {errors.lastName}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{
                display: 'block',
                color: '#ccc',
                fontSize: '12px',
                marginBottom: '5px',
                fontWeight: '500'
              }}>
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: `1px solid ${errors.email ? '#ff6b6b' : 'rgba(255, 255, 255, 0.2)'}`,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter your email address"
              />
              {errors.email && (
                <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '3px' }}>
                  {errors.email}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{
                display: 'block',
                color: '#ccc',
                fontSize: '12px',
                marginBottom: '5px',
                fontWeight: '500'
              }}>
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: `1px solid ${errors.phone ? '#ff6b6b' : 'rgba(255, 255, 255, 0.2)'}`,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="(555) 123-4567"
              />
              {errors.phone && (
                <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '3px' }}>
                  {errors.phone}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                color: '#ccc',
                fontSize: '12px',
                marginBottom: '5px',
                fontWeight: '500'
              }}>
                Where did you hear about The Hub? (Optional)
              </label>
              <input
                type="text"
                value={formData.heardOn}
                onChange={handleInputChange('heardOn')}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: `1px solid ${errors.heardOn ? '#ff6b6b' : 'rgba(255, 255, 255, 0.2)'}`,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="e.g., LinkedIn, Google, Friend, etc."
              />
              {errors.heardOn && (
                <div style={{ color: '#ff6b6b', fontSize: '11px', marginTop: '3px' }}>
                  {errors.heardOn}
                </div>
              )}
            </div>

            {generalError && (
              <div style={{
                backgroundColor: 'rgba(255, 107, 107, 0.1)',
                border: '1px solid #ff6b6b',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '15px',
                color: '#ff6b6b',
                fontSize: '13px'
              }}>
                {generalError}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={handleClose}
                style={{
                  backgroundColor: 'transparent',
                  color: '#ccc',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  backgroundColor: '#4ecdc4',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isSubmitting ? 0.7 : 1
                }}
              >
                {isSubmitting ? 'Signing up...' : 'Join Beta'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
    )}
    </>
  );
};

export default BetaSignupForm;